import React, { useCallback, useEffect, useState } from 'react';
import { Currency, Tooltip, Widget } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import BudgetTooltip from '../components/BudgetTooltip';
import PlannedBudgetTooltip from '../components/PlannedBudgetTooltip';
import PastDueTooltip from '../components/PastDueTooltip';
import {
  Box,
  Boxes,
  ChartContainer,
  Label,
  LowerBoxes,
  Metric,
  SmallBox,
  UpperBox,
  Value,
  ValueContainer,
} from '../components/StyledComponents';
import ViewNav from '../components/ViewNav';
import ExpensesRevenueByMonthChart from './ExpensesRevenueByMonthChart';
import ExpensesRevenueProgressChart from './ExpensesRevenueProgressChart';
import { useCurrencyFormat } from '~/hooks';

export default function ExpensesRevenueProgress({ project, view, actualsThrough, approvalStatuses, onViewChange }) {
  const { workspace } = useWorkspace();

  const api = useApi();
  const [{ data, isReady }, setQuery] = useState({ data: null, isReady: false });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .dashboard()
        .progress.expensesRevenue({
          actualsThrough,
          approvalStatusId: approvalStatuses?.map(({ id }) => id),
        });
      setQuery({ data, isReady: true });
    } catch (error) {
      setQuery({ data: null, isReady: true });
    }
  }, [workspace.id, project.id, api, actualsThrough, approvalStatuses]);

  useEffect(() => {
    fetchData();
  }, [fetchData, project]);

  const boxes = {
    budget: !!data?.budget,
    forecast: data?.forecast?.dates.length > 0,
    get any() {
      return this.budget || this.forecast;
    },
  };

  const currency = project.currency;
  const currencyFormat = useCurrencyFormat({ minimumFractionDigits: 0, maximumFractionDigits: 0, currency });

  return (
    <Widget loading={!isReady}>
      <Widget.Preview style={{ minHeight: '32.4rem' }} />
      <Widget.Content>
        <ViewNav view={view} onViewChange={onViewChange} />

        {data && (
          <>
            <ChartContainer>
              {data &&
                {
                  progress: () => <ExpensesRevenueProgressChart project={project} data={data} />,
                  month: () => <ExpensesRevenueByMonthChart project={project} data={data} />,
                }[view]()}
            </ChartContainer>

            <Boxes>
              {project.permissions.viewRevenue && project.isBillable && (
                <Box>
                  <UpperBox>
                    <ValueContainer>
                      <Value>
                        <Currency
                          value={data.actual.revenue}
                          currency={currency}
                          minimumFractionDigits={0}
                          maximumFractionDigits={0}
                        />
                      </Value>
                      {project.revenueRecognitionMethod === 'invoiced' &&
                        ['fixed', 'fixed_recurring'].includes(project.billingTypeId) && (
                          <PastDueTooltip value={data.pastDue} currency={project.currency} />
                        )}
                    </ValueContainer>
                    <Label>Actual Expenses Revenue</Label>
                  </UpperBox>

                  {boxes.any && (
                    <LowerBoxes>
                      {boxes.budget && (
                        <SmallBox>
                          <Metric>
                            <Value>
                              <Currency
                                value={data.budget.revenue.estimated ?? 0}
                                currency={currency}
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                              />
                            </Value>
                            <Label>Expenses Revenue Budget</Label>
                          </Metric>
                          <Metric status={data.budget.revenue.status}>
                            <BudgetTooltip value={data.budget.revenue.percent}>
                              <Value>
                                <Currency
                                  value={data.budget.revenue.remaining}
                                  currency={currency}
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                />
                              </Value>
                              <Label>Expenses Revenue Budget Left</Label>
                            </BudgetTooltip>
                          </Metric>
                        </SmallBox>
                      )}

                      {boxes.forecast && (
                        <SmallBox>
                          <Metric>
                            <Tooltip
                              message={`${currencyFormat.format(data.actual.revenue)} actual revenue plus ${currencyFormat.format(data.forecast.revenue.left)} remaining plan revenue.`}
                              placement="top">
                              <Value>
                                <Currency
                                  value={data.forecast.revenue.forecasted}
                                  currency={currency}
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                />
                              </Value>
                              <Label>Forecast</Label>
                            </Tooltip>
                          </Metric>
                          <Metric status={data.forecast.revenue.status}>
                            <PlannedBudgetTooltip
                              value={data.forecast.revenue}
                              currency={currency}
                              metric="expenses revenue">
                              <Value>
                                <Currency
                                  value={data.forecast.revenue.left}
                                  currency={currency}
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                />
                              </Value>
                              <Label>Plan Left</Label>
                            </PlannedBudgetTooltip>
                          </Metric>
                        </SmallBox>
                      )}
                    </LowerBoxes>
                  )}
                </Box>
              )}
            </Boxes>
          </>
        )}
      </Widget.Content>
    </Widget>
  );
}
